import {
  usePostOperatorsClientsMutation,
  usePutOperatorsClientsMutation,
} from '@/api/queries/operators';
import { countryOfOriginOptions } from '@/utils/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@mui/material';
import { Stack } from '@mui/system';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  clientDtoSchema,
  ClientSchemaType,
  ClientType,
  ControlledField,
  LanguageOptions,
  PhoneInputField,
  SelectInputField,
} from 'remote/global';

const defaultCountryCode = {
  [LanguageOptions.en]: 'EE',
  [LanguageOptions.et]: 'EE',
  [LanguageOptions.ru]: 'EE',
  [LanguageOptions.lv]: 'LV',
};

function MyClientsRequestForm({
  defaultValues,
  onClose,
  onSubmitSuccess,
}: {
  defaultValues?: ClientSchemaType;
  onClose?: () => void;
  onSubmitSuccess?: (data: ClientSchemaType) => void;
}) {
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const defaultCountry = defaultCountryCode[i18n.language as LanguageOptions];

  const isEditMode = defaultValues?.id != null;

  const formInstance = useForm<ClientSchemaType>({
    defaultValues: {
      country: defaultCountry,
      type: ClientType.BUSINESS,
      lang: i18n.language,
      ...defaultValues,
    },
    resolver: zodResolver(clientDtoSchema),
  });
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  const postClientsMutation = usePostOperatorsClientsMutation();
  const putClientsMutation = usePutOperatorsClientsMutation();

  const onValid = async (data: ClientSchemaType) => {
    let response: ClientSchemaType;
    if (isEditMode) response = await putClientsMutation.mutateAsync(data);
    else response = await postClientsMutation.mutateAsync(data);
    toast.success(t('Toast.success'));
    onSubmitSuccess?.(response);
    onClose?.();
  };

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Stack direction='column' spacing={1}>
        <ControlledField
          control={control}
          name='country'
          defaultValue=''
          render={(renderProps) => (
            <SelectInputField {...renderProps} options={countryOfOriginOptions} />
          )}
          focusInitial
        />
        <ControlledField control={control} defaultValue='' name='regCode' />
        <ControlledField control={control} defaultValue='' name='companyName' />
        <ControlledField control={control} defaultValue='' name='legalName' />
        <ControlledField control={control} defaultValue='' name='companyVat' />
        <ControlledField control={control} defaultValue='' name='address' />
        <ControlledField
          control={control}
          name='companyEmail'
          defaultValue=''
          fieldProps={{
            helperText: '',
            InputProps: {
              type: 'email',
              autoComplete: 'email',
            },
          }}
        />
        <ControlledField
          control={control}
          name='companyPhone'
          defaultValue=''
          render={(renderProps) => <PhoneInputField {...renderProps} />}
        />
      </Stack>
      <Stack direction='column' spacing={1}>
        <ControlledField
          control={control}
          defaultValue=''
          name='contactInfo.firstName'
          translationKey='firstName'
        />
        <ControlledField
          control={control}
          defaultValue=''
          name='contactInfo.lastName'
          translationKey='lastName'
        />
        <ControlledField
          control={control}
          defaultValue=''
          name='contactInfo.email'
          translationKey='email'
          fieldProps={{
            helperText: '',
            InputProps: {
              type: 'email',
              autoComplete: 'email',
            },
          }}
        />
        <ControlledField
          control={control}
          defaultValue=''
          name='contactInfo.phone'
          render={(renderProps) => <PhoneInputField {...renderProps} translationKey='phone' />}
        />
      </Stack>
      <Button
        sx={{ mt: 2, float: 'right' }}
        type='submit'
        variant='contained'
        color='primary'
        disableElevation
        disabled={isSubmitting || isValidating || isLoading}
      >
        {isEditMode ? t('save') : t('actions.user.sendContract')}
      </Button>
    </form>
  );
}

export default MyClientsRequestForm;
